import { ArtifactTile, ArtifactTileSkeleton } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness (added by the screenshot-verify pass for #124 / PR #142;
 * Onyx hex+notch material adopted for #162 on top of #158).
 * One tile for every Brain citizen: experiences (per-type icon color) + the
 * neutral supporting kinds, across the lifecycle states, plus fresh / active /
 * skeleton. The cursor glow (BorderGlow track="cursor") shows on hover.
 *
 * #162 / #158 sign-off gate (verify in a real browser, light + .dark): the tile
 * now rides the `nx-s` notched silhouette with a CONTINUOUS NxKeyline — the line
 * must trace the FULL outline incl. the top-right notch chamfer with ZERO gaps,
 * at rest AND active (accent line + glow). The status dots are the tightened
 * flat-top hex. The skeleton matches the notched footprint so a loading grid
 * does not reflow.
 */
export function ArtifactTileDemo() {
  return (
    <Section
      id="artifact-tile"
      title="ArtifactTile"
      description="One card for every citizen of the Brain (unified#103), now on the Onyx hex+notch material (#158): nx-s silhouette, clip-safe elev-card, a continuous NxKeyline outline (rest + active), flat-top hex status dots. Per-type color lives ONLY in the 28×28 icon tile; title/meta/keyline/glow stay monochrome. Status via the #125 StatusChip vocabulary. Cursor glow on hover; transform-only entrance."
    >
      <VariantBlock label="Onyx material — notched silhouette + continuous keyline (rest · active · hover the cursor glow)">
        <div className="grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
          <ArtifactTile kind="experience" experienceType="quest" status="ready" title="Resting keyline" meta="nx-s · neutral hairline" />
          <ArtifactTile kind="experience" experienceType="note" status="published" title="Active keyline + glow" meta="the router target" active />
          <ArtifactTile kind="file" status="ready" title="Hover for the cursor glow" meta="masked ring · clipped to notch" />
        </div>
      </VariantBlock>

      <VariantBlock label="Experiences — per-type identity color (icon tile only)">
        <div className="grid w-full max-w-4xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <ArtifactTile kind="experience" experienceType="quest" status="published" title="Welcome Quest" meta="Quiz · 12 questions" />
          <ArtifactTile kind="experience" experienceType="note" status="ready" title="Q3 Strategy Note" meta="Note · 4 sections" />
          <ArtifactTile kind="experience" experienceType="podcast" status="generating" title="Founder Podcast Ep. 4" meta="Audio · generating" />
          <ArtifactTile kind="experience" experienceType="video" status="draft" title="Launch Teaser Cut" meta="Video · 0:42" />
          <ArtifactTile kind="experience" experienceType="expert" status="failed" title="Tax Advisor Bot" meta="Expert · import error" />
        </div>
      </VariantBlock>

      <VariantBlock label="Supporting kinds — fully neutral (no hue)">
        <div className="grid w-full max-w-4xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <ArtifactTile kind="file" status="ready" title="Pitch Deck 2026.pdf" meta="PDF · 18 pages" />
          <ArtifactTile kind="chart" status="ready" title="Revenue by Quarter" meta="Chart · bar" />
          <ArtifactTile kind="analysis" status="generating" title="Sentiment Analysis" meta="Indexing 3 of 7" />
          <ArtifactTile kind="link" status="ready" title="docs.aisquare.studio" meta="used in 3 turns" />
          <ArtifactTile kind="connector" status="syncing" title="Notion Workspace" meta="Connector · syncing" />
          <ArtifactTile kind="fetched" status="ready" title="competitor-pricing.html" meta="Fetched · web" />
        </div>
      </VariantBlock>

      <VariantBlock label="fresh (New badge) · active (focus ring) · skeleton (loading)">
        <div className="grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
          <ArtifactTile kind="experience" experienceType="quest" status="ready" title="Just-made Quest" meta="created this turn" fresh />
          <ArtifactTile kind="experience" experienceType="note" status="published" title="Focused Note" meta="the router target" active />
          <ArtifactTileSkeleton />
          <ArtifactTileSkeleton />
        </div>
      </VariantBlock>
    </Section>
  );
}
